import React from "react";
import { Form } from "react-crud-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { PermissionControlledInput } from "permissions/components/input";
import { FormLabel } from "components/form";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import { Input } from "ui/components/input";
import { Switch } from "ui/components/switch";
import { DatePicker } from "components/date-picker-2";
import { StatusTabInput } from "components/status/tabs/input";
import {
  addItemToCollection,
  updateItemInCollection,
} from "directus/src/operations";
import { quadrimesterSchema } from "./schema";
import { FormActions } from "components/form/actions";
import { fetchQuadDataforForm } from "./read";
import { useParams } from "react-router-dom";
import { invalidateAllQueries } from "utils/utils";
import { FormColumns } from "components/form";
import { eventBus } from "utils/eventBus";

export default function QuadrimesterForm() {
  const navigate = useNavigate();
  const { quadId } = useParams();

  const form = useForm({
    resolver: zodResolver(quadrimesterSchema),
    defaultValues: {
      name: "",
      start_date: undefined,
      end_date: undefined,
      status: "upcoming",
      allow_self_nomination: true,
    },
  });

  async function handleCreate(context) {
    const values = context.values;
    await addItemToCollection("performance_review_months", values);
    eventBus.emit("refresh:list", "performance_reviews");
    invalidateAllQueries();
    navigate(-1);
  }

  async function handleRead(id) {
    const response = await fetchQuadDataforForm({ id });
    return response;
  }

  async function handleUpdate(id, context) {
    const values = context.dirtyValues;
    await updateItemInCollection("performance_review_months", id, values);
    eventBus.emit("refresh:list", "performance_reviews");
    navigate("/performance/review");
    invalidateAllQueries();
  }

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={quadId}
      schema={quadrimesterSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isDirty,
        isUpdating,
        updateItem,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              createItem();
            }
          },
          { preventDefault: true },
        );

        return (
          <div className="space-y-6 max-w-xl">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={
                      isNewItem
                        ? "create:performance_review_months"
                        : "update:performance_review_months"
                    }
                    collection="performance_review_months"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel>Status</FormLabel>
                    <StatusTabInput
                      {...field}
                      collection="performance_review_months"
                      sort={true}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={
                      isNewItem
                        ? "create:performance_review_months"
                        : "update:performance_review_months"
                    }
                    collection="performance_review_months"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? "create:performance_review_months"
                          : "update:performance_review_months"
                      }
                      collection="performance_review_months"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel>Start Date</FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? "create:performance_review_months"
                          : "update:performance_review_months"
                      }
                      collection="performance_review_months"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel>End Date</FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="allow_self_nomination"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={
                      isNewItem
                        ? "create:performance_review_months"
                        : "update:performance_review_months"
                    }
                    collection="performance_review_months"
                    type="form"
                    field={field.name}
                  >
                    <div className="flex items-center justify-between">

                      <FormLabel className="mb-0">Allow Self Nomination</FormLabel>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              isDirty={isDirty}
              createItem={createItem}
              updateItem={updateItem}
              teleport={true}
            />
          </div>
        );
      }}
    </Form>
  );
}
